const encoder = new TextEncoder()

export const WEB_LOG_CONFIG = {
  BATCH_SIZE: 50,
  MAX_BATCH_BYTES: 64 * 1024,
  MAX_EXTRA_BYTES: 16 * 1024,
  MAX_QUEUE_SIZE: 200,
  MIN_FLUSH_DELAY: 15 * 1000,
  FLUSH_JITTER: 15 * 1000,
  SUMMARY_INTERVAL: 60 * 1000
}

export const WEB_SUPPRESSED_MESSAGES = new Set([
  'MemberStatusService recvMGLiveRoomCustomMsg',
  'onReciveInitUserStatus',
  'onReciveUserStatusChange',
  'updateMemberStatusMap-start',
  'updateMemberStatusMap processing',
  'updateMemberStatusMap no change',
  'updateMemberStatusMap EventBus emitted'
])

const getJsonBytes = value => encoder.encode(JSON.stringify(value)).length

export const shouldSuppressWebLog = ({ level = 'info', message = '' } = {}) =>
  level === 'info' && WEB_SUPPRESSED_MESSAGES.has(message)

export const sanitizeWebLogExtra = (
  extra = {},
  maxBytes = WEB_LOG_CONFIG.MAX_EXTRA_BYTES
) => {
  const clonedExtra = JSON.parse(JSON.stringify(extra))
  const originalSize = getJsonBytes(clonedExtra)
  if (originalSize <= maxBytes) {
    return clonedExtra
  }

  const diagnosticKeys = [
    'action',
    'component',
    'source',
    'userId',
    'fromUserId',
    'type',
    'roomId'
  ]
  const reducedExtra = diagnosticKeys.reduce((result, key) => {
    if (clonedExtra[key] !== undefined) {
      result[key] = clonedExtra[key]
    }
    return result
  }, {})

  return {
    ...reducedExtra,
    extraTruncated: true,
    originalSize
  }
}

export const takeWebUploadBatch = (
  queue,
  {
    maxCount = WEB_LOG_CONFIG.BATCH_SIZE,
    maxBytes = WEB_LOG_CONFIG.MAX_BATCH_BYTES
  } = {}
) => {
  const logs = []
  for (const log of queue) {
    if (logs.length >= maxCount) {
      break
    }

    const candidate = [...logs, log]
    if (getJsonBytes(candidate) > maxBytes && logs.length > 0) {
      break
    }
    logs.push(log)
  }

  return {
    logs,
    consumedCount: logs.length,
    byteSize: getJsonBytes(logs)
  }
}

export const getWebFlushDelay = (random = Math.random) =>
  WEB_LOG_CONFIG.MIN_FLUSH_DELAY +
  Math.round(WEB_LOG_CONFIG.FLUSH_JITTER * random())

export const enqueueWebLog = (
  queue,
  log,
  maxSize = WEB_LOG_CONFIG.MAX_QUEUE_SIZE
) => {
  const nextQueue = [...queue]
  if (nextQueue.length >= maxSize) {
    const oldestInfoIndex = nextQueue.findIndex(item => item.level === 'info')
    nextQueue.splice(oldestInfoIndex >= 0 ? oldestInfoIndex : 0, 1)
  }
  nextQueue.push(log)
  return nextQueue
}

export const createSuppressedLogCounter = () => {
  let suppressedCount = 0
  let messageCounts = {}

  return {
    record(message) {
      suppressedCount += 1
      messageCounts[message] = (messageCounts[message] || 0) + 1
    },
    takeSummary() {
      if (suppressedCount === 0) {
        return null
      }
      const summary = {
        suppressedCount,
        messageCounts: { ...messageCounts }
      }
      suppressedCount = 0
      messageCounts = {}
      return summary
    },
    reset() {
      suppressedCount = 0
      messageCounts = {}
    }
  }
}
